import {
  AdClickedPayload,
  AdErrorPayload,
  AdManifestLoadedPayload,
  AdModuleAPI,
  AdQuartile,
  AdQuartilePayload,
} from '../adapters/internal/ads/AdModuleAPI';
import {AdBreakInfo} from '../types/ads/AdBreakInfo';
import {AdInfo} from '../types/ads/AdInfo';
import type {AdSample} from '../types/AdSample';
import {logger} from '../utils/Logger';
import {isFiniteNumber} from '../utils/numberUtils';
import {buildAdSample} from '../utils/sampleUtils';
import * as Utils from '../utils/Utils';
import {isNotNullish, isNullish, tryJSONStringify} from '../utils/Utils';
import {generateUUID} from '../utils/uuidUtils';
import {VERSION} from '../utils/Version';
import {ViewportTracker} from '../utils/ViewportTracker';

import {Analytics} from './Analytics';

export class AdAnalytics {
  private readonly analytics: Analytics;
  private readonly adapter: AdModuleAPI;
  private readonly viewportTracker?: ViewportTracker;

  private activeAdBreakState: ActiveAdBreakState | undefined;
  private activeAdState: ActiveAdState | undefined;

  private adManifestDownloadTime: number | undefined;
  private adBreakStartTimestamp?: number;
  private beginPlayingTimestamp?: number;
  private enterViewportTimestamp?: number;
  private isPlaying = false;
  private currentTime: number = 0;
  private currentTimeInterval: ReturnType<typeof setInterval> | undefined;
  private adPodPosition = 0;

  private prerollAdPlayAttemptTimestamp: number | undefined;

  constructor(analytics: Analytics, adapter: AdModuleAPI) {
    this.analytics = analytics;
    this.adapter = adapter;

    this.adapter.adCallbacks.onAdStarted = (event) => this.onAdStarted(event);
    this.adapter.adCallbacks.onAdFinished = (event) => this.onAdFinished(event);
    this.adapter.adCallbacks.onAdBreakStarted = (event) => this.onAdBreakStarted(event);
    this.adapter.adCallbacks.onAdBreakFinished = (event) => this.onAdBreakFinished(event);
    this.adapter.adCallbacks.onAdClicked = (event) => this.onAdClicked(event);
    this.adapter.adCallbacks.onAdError = (event) => this.onAdError(event);
    this.adapter.adCallbacks.onAdManifestLoaded = (event) => this.onAdManifestLoaded(event);
    this.adapter.adCallbacks.onPlay = () => this.onPlay();
    this.adapter.adCallbacks.onPlayBeforeStartupHasSucceeded = () => this.onPlayBeforeStartupHasSucceeded();
    this.adapter.adCallbacks.onPause = () => this.onPause();
    this.adapter.adCallbacks.onAdAbandonment = (isClosing) => this.onAdAbandonment(isClosing);
    this.adapter.adCallbacks.onAdSkipped = (event) => this.onAdSkipped(event);
    this.adapter.adCallbacks.onAdQuartile = (event) => this.onAdQuartile(event);

    const container = this.adapter.getContainer();
    if (isNotNullish(container)) {
      this.viewportTracker = new ViewportTracker(container, () => this.onIntersectionChanged(), 0.5);
    }
  }

  release() {
    this.resetActiveAd();
    this.viewportTracker?.dispose();
    this.adManifestDownloadTime = undefined;
    this.prerollAdPlayAttemptTimestamp = undefined;
  }

  onIntersectionChanged() {
    if (!this.activeAdState) {
      return;
    }
    if (this.isContainerInViewport()) {
      this.enterViewportTimestamp = Date.now();
    } else {
      if (this.enterViewportTimestamp) {
        this.activeAdState.timeInViewport =
          (this.activeAdState.timeInViewport || 0) + Date.now() - this.enterViewportTimestamp;
      }
    }
  }

  isContainerInViewport(): boolean {
    return this.viewportTracker ? this.viewportTracker.isInViewport() : true;
  }

  onPlay() {
    if (this.adapter && this.adapter.isLinearAdActive() && this.activeAdState) {
      const timestamp = Date.now();
      this.beginPlayingTimestamp = timestamp;
      this.enterViewportTimestamp = timestamp;
      this.isPlaying = true;
    }
  }

  onPlayBeforeStartupHasSucceeded() {
    // only count the first play event before an adBreak to track the adStartupTime correctly
    if (isNullish(this.prerollAdPlayAttemptTimestamp)) {
      this.prerollAdPlayAttemptTimestamp = Date.now();
    }
  }

  onPause() {
    if (this.adapter && this.adapter.isLinearAdActive() && this.activeAdState) {
      this.updatePlayingTime(this.activeAdState);
      this.isPlaying = false;
    }
  }

  onAdManifestLoaded(payload: AdManifestLoadedPayload) {
    if (payload.isVmap) {
      this.sendAdSample(
        buildInitialActiveAdBreakState({...payload.adBreakInfo, manifestDownloadTime: payload.manifestDownloadTime}),
        buildInitialActiveAdState(),
      );
      return;
    }

    this.adManifestDownloadTime = payload.manifestDownloadTime;
  }

  onAdBreakStarted(adBreak: AdBreakInfo) {
    this.adPodPosition = 0;
    this.activeAdBreakState = buildInitialActiveAdBreakState({
      ...adBreak,
      manifestDownloadTime: this.adManifestDownloadTime,
    });
    delete this.adManifestDownloadTime;
    this.adBreakStartTimestamp = Date.now();
  }

  onAdBreakFinished(_adBreak: AdBreakInfo) {
    this.resetActiveAd();
    this.activeAdBreakState = undefined;
    this.prerollAdPlayAttemptTimestamp = undefined;
  }

  onAdStarted(adInfo: AdInfo) {
    if (!adInfo.isLinear) {
      return;
    }

    this.resetActiveAd();

    this.activeAdState = buildInitialActiveAdState(adInfo);
    const now = Date.now();
    if (isNotNullish(this.prerollAdPlayAttemptTimestamp) && this.activeAdBreakState?.adPosition === 'pre') {
      this.activeAdState.adStartupTime = now - this.prerollAdPlayAttemptTimestamp;
      // reset the timestamp since only the first ad of the ad break should have the value added to the adStartupTime
      this.prerollAdPlayAttemptTimestamp = undefined;
    } else {
      this.activeAdState.adStartupTime = this.adBreakStartTimestamp ? now - this.adBreakStartTimestamp : undefined;
    }
    this.activeAdState.started = 1;
    this.activeAdState.timePlayed = 0;
    this.activeAdState.timeInViewport = 0;
    this.activeAdState.percentageInViewport = 0;
    this.activeAdState.adPodPosition = this.adPodPosition;

    this.currentTime = 0;
    this.currentTimeInterval = setInterval(() => {
      try {
        if (
          this.activeAdState?.adDuration != null &&
          this.activeAdState.adDuration > 0 &&
          this.adapter.isLinearAdActive()
        ) {
          this.currentTime = Utils.calculateTime(Math.max(this.adapter.currentTime(), 0));
        }
      } catch (e) {
        logger.log('AdStarted monitoring interval failed and got cleared', e);
        this.resetActiveAd();
      }
    }, 100);

    const timestamp = Date.now();
    this.beginPlayingTimestamp = timestamp;
    this.enterViewportTimestamp = this.isContainerInViewport() ? timestamp : undefined;
    this.isPlaying = true;
    this.adPodPosition++;
  }

  onAdFinished(_ad: AdInfo) {
    if (!this.activeAdBreakState || !this.activeAdState) {
      return;
    }

    const activeAdStateCopy = {...this.activeAdState};
    activeAdStateCopy.completed = 1;
    this.resetActiveAd();
    this.sendAdSample(this.activeAdBreakState, activeAdStateCopy, activeAdStateCopy.adDuration);
  }

  onAdSkipped(_ad: AdInfo) {
    if (!this.activeAdBreakState || !this.activeAdState) {
      return;
    }

    const activeAdStateCopy = {...this.activeAdState};
    activeAdStateCopy.skipped = 1;
    activeAdStateCopy.skipPosition = this.currentTime;
    activeAdStateCopy.skipPercentage = Utils.calculatePercentage(
      activeAdStateCopy.skipPosition,
      this.activeAdState.adDuration,
    );
    this.resetActiveAd();
    this.sendAdSample(this.activeAdBreakState, activeAdStateCopy, activeAdStateCopy.skipPosition);
  }

  onAdError(payload: AdErrorPayload) {
    const adBreakSample = buildInitialActiveAdBreakState(payload.adBreakInfo);

    const transformedError = this.analytics.transformError(
      {code: payload.code, message: payload.message, severity: 'CRITICAL'},
      payload,
    );

    adBreakSample.errorCode = transformedError.code;
    adBreakSample.errorData = tryJSONStringify({code: payload.code, message: payload.message});
    adBreakSample.errorMessage = transformedError.message;
    adBreakSample.errorSeverity = transformedError.severity;

    let activeAdState = buildInitialActiveAdState();
    let exitPosition: number | undefined;
    if (this.activeAdState && payload.activeAdId && this.activeAdState.adId === payload.activeAdId) {
      activeAdState = this.activeAdState;
      exitPosition = this.currentTime;
      activeAdState.errorPosition = this.currentTime;
      activeAdState.errorPercentage = Utils.calculatePercentage(activeAdState.errorPosition, activeAdState.adDuration);
    }
    this.sendAdSample(adBreakSample, activeAdState, exitPosition);
  }

  onAdClicked(payload: AdClickedPayload) {
    if (!this.activeAdState) {
      return;
    }
    this.activeAdState.adClickthroughUrl = payload.clickThroughUrl;
    this.activeAdState.clicked = 1;
    this.activeAdState.clickPosition = this.currentTime;
    this.activeAdState.clickPercentage = Utils.calculatePercentage(
      this.activeAdState.clickPosition,
      this.activeAdState.adDuration,
    );
  }

  onAdQuartile(payload: AdQuartilePayload) {
    if (!this.activeAdState) {
      return;
    }
    if (payload.quartile === AdQuartile.FIRST_QUARTILE) {
      this.activeAdState.quartile1 = 1;
    } else if (payload.quartile === AdQuartile.MIDPOINT) {
      this.activeAdState.midpoint = 1;
    } else if (payload.quartile === AdQuartile.THIRD_QUARTILE) {
      this.activeAdState.quartile3 = 1;
    }
  }

  onAdAbandonment(isClosing: boolean) {
    if (!this.activeAdBreakState || !this.activeAdState) {
      return;
    }

    const activeAdStateCopy = {...this.activeAdState};
    activeAdStateCopy.closed = 1;
    activeAdStateCopy.closePosition = this.currentTime;
    activeAdStateCopy.closePercentage = Utils.calculatePercentage(
      activeAdStateCopy.closePosition,
      activeAdStateCopy.adDuration,
    );
    this.resetActiveAd();
    this.sendAdSample(this.activeAdBreakState, activeAdStateCopy, activeAdStateCopy.closePosition, isClosing);
    this.activeAdBreakState = undefined;
    // important to reset because of source changes
    this.prerollAdPlayAttemptTimestamp = undefined;
  }

  private updatePlayingTime(activeAdState: Pick<AdSample, 'percentageInViewport' | 'timeInViewport' | 'timePlayed'>) {
    const timestamp = Date.now();
    if (this.beginPlayingTimestamp && this.isPlaying) {
      if (activeAdState.timePlayed !== undefined) {
        activeAdState.timePlayed += timestamp - this.beginPlayingTimestamp;
      }
      if (activeAdState.timeInViewport !== undefined && this.isContainerInViewport() && this.enterViewportTimestamp) {
        activeAdState.timeInViewport += timestamp - this.enterViewportTimestamp;
      }
    }

    activeAdState.percentageInViewport = Utils.calculatePercentage(
      activeAdState.timeInViewport,
      activeAdState.timePlayed,
    );
  }

  private sendAdSample(
    activeAdBreakState: ActiveAdBreakState,
    activeAdState: ActiveAdState,
    exitPosition: number | undefined = undefined,
    isClosing: boolean = false,
  ) {
    const adSample: AdSample = {
      // set common fields from playback sample (if already created)
      ...buildAdSample(this.analytics.sample),
      // set ad break fields
      ...activeAdBreakState,
      // set ad fields
      ...activeAdState,
    };

    adSample.platform = 'web';
    adSample.analyticsVersion = VERSION;
    const moduleInfo = this.adapter.getAdModuleInfo();
    adSample.adModule = moduleInfo.name;
    adSample.adModuleVersion = moduleInfo.version;
    adSample.autoplay = this.adapter.getAutoPlay();
    adSample.pageLoadType = Utils.getPageLoadType();

    adSample.time = Date.now();
    adSample.adImpressionId = generateUUID();

    adSample.exitPosition = exitPosition;
    adSample.playPercentage = Utils.calculatePercentage(adSample.exitPosition, adSample.adDuration);
    this.updatePlayingTime(adSample);

    // Fallback to set quartile properties (if not set yet)
    // For players that don't emit native quartile events (THEOplayer) we calculate quartile properties based on playPercentage
    if (isNotNullish(adSample.playPercentage)) {
      if (adSample.quartile1 === 0 && adSample.playPercentage >= 25) {
        adSample.quartile1 = 1;
      }
      if (adSample.midpoint === 0 && adSample.playPercentage >= 50) {
        adSample.midpoint = 1;
      }
      if (adSample.quartile3 === 0 && adSample.playPercentage >= 75) {
        adSample.quartile3 = 1;
      }
    }

    this.adBreakStartTimestamp = Date.now();
    this.isPlaying = false;

    if (isClosing) {
      this.analytics.backend.sendUnloadAdRequest(adSample);
    } else {
      this.analytics.backend.sendAdRequest(adSample);
    }
  }

  private resetActiveAd() {
    clearInterval(this.currentTimeInterval);
    this.currentTime = 0;
    this.activeAdState = undefined;
  }
}

/**
 * Used to track the state of the active ad break.
 *
 * It is partial of {@link AdSample} because it's purpose is to build the final {@link AdSample}.
 */
type ActiveAdBreakState = Pick<
  AdSample,
  | 'adFallbackIndex'
  | 'adIdPlayer'
  | 'adIsPersistent'
  | 'adOffset'
  | 'adPosition'
  | 'adPreloadOffset'
  | 'adReplaceContentDuration'
  | 'adScheduleTime'
  | 'adTagPath'
  | 'adTagServer'
  | 'adTagType'
  | 'adTagUrl'
  | 'errorCode'
  | 'errorData'
  | 'errorMessage'
  | 'errorSeverity'
  | 'manifestDownloadTime'
>;

export function buildInitialActiveAdBreakState(adBreakInfo?: AdBreakInfo): ActiveAdBreakState {
  const state: ActiveAdBreakState = {adFallbackIndex: 0};

  if (isNullish(adBreakInfo)) {
    return state;
  }

  state.adIdPlayer = adBreakInfo.id;
  state.adReplaceContentDuration = Utils.calculateTimeWithUndefined(adBreakInfo.replaceContentDuration);
  state.adIsPersistent = adBreakInfo.persistent;
  state.adTagType = adBreakInfo.tagType;
  state.adTagUrl = adBreakInfo.tagUrl;
  state.adPreloadOffset = Utils.calculateTimeWithUndefined(adBreakInfo.preloadOffset);
  state.adFallbackIndex = adBreakInfo.fallbackIndex ?? 0;
  state.manifestDownloadTime = Utils.calculateTimeWithUndefined(adBreakInfo.manifestDownloadTime);

  if (adBreakInfo.position === 'pre' || adBreakInfo.position === 'post') {
    state.adPosition = adBreakInfo.position;
  } else if (isNotNullish(adBreakInfo.position)) {
    state.adPosition = 'mid';
    state.adOffset = adBreakInfo.position;
  }

  if (isNotNullish(state.adTagUrl)) {
    try {
      const adTagUrl = new URL(state.adTagUrl);
      state.adTagServer = adTagUrl.hostname;
      state.adTagPath = adTagUrl.pathname;
    } catch {
      // Invalid URL, leave fields undefined
    }
  }

  if (isFiniteNumber(adBreakInfo.scheduleTime)) {
    state.adScheduleTime = Utils.calculateTime(adBreakInfo.scheduleTime);
  }

  return state;
}

/**
 * Used to track the state of the active ad in ad break.
 *
 * It is partial of {@link AdSample} because it's purpose is to build the final {@link AdSample}.
 */
type ActiveAdState = Pick<
  AdSample,
  | 'adClickthroughUrl'
  | 'adDescription'
  | 'adDuration'
  | 'adId'
  | 'adPlaybackHeight'
  | 'adPlaybackWidth'
  | 'adPodPosition'
  | 'adSkipAfter'
  | 'adSkippable'
  | 'adStartupTime'
  | 'adSystem'
  | 'adTitle'
  | 'advertiserName'
  | 'apiFramework'
  | 'clicked'
  | 'clickPercentage'
  | 'clickPosition'
  | 'closed'
  | 'closePercentage'
  | 'closePosition'
  | 'completed'
  | 'creativeAdId'
  | 'creativeId'
  | 'errorPercentage'
  | 'errorPosition'
  | 'isLinear'
  | 'mediaPath'
  | 'mediaServer'
  | 'mediaUrl'
  | 'midpoint'
  | 'minSuggestedDuration'
  | 'percentageInViewport'
  | 'quartile1'
  | 'quartile3'
  | 'skipped'
  | 'skipPercentage'
  | 'skipPosition'
  | 'started'
  | 'streamFormat'
  | 'surveyUrl'
  | 'timeInViewport'
  | 'timePlayed'
  | 'universalAdIdRegistry'
  | 'universalAdIdValue'
  | 'videoBitrate'
  | 'wrapperAdsCount'
>;

export function buildInitialActiveAdState(adInfo?: AdInfo): ActiveAdState {
  const state: ActiveAdState = {
    clicked: 0,
    closed: 0,
    completed: 0,
    midpoint: 0,
    quartile1: 0,
    quartile3: 0,
    skipped: 0,
    started: 0,
  };

  if (isNullish(adInfo)) {
    return state;
  }

  state.adClickthroughUrl = adInfo.clickThroughUrl;
  state.adId = adInfo.id;
  state.mediaUrl = adInfo.mediaUrl;
  state.isLinear = adInfo.isLinear;
  state.adPlaybackHeight = adInfo.height;
  state.adPlaybackWidth = adInfo.width;
  state.streamFormat = adInfo.mimeType;

  if (isNotNullish(state.mediaUrl)) {
    try {
      const mediaUrl = new URL(state.mediaUrl);
      state.mediaPath = mediaUrl.pathname;
      state.mediaServer = mediaUrl.hostname;
    } catch {
      // Invalid URL, leave fields undefined
    }
  }

  if (isNotNullish(adInfo.bitrate)) {
    state.videoBitrate = adInfo.bitrate * 1000;
  }

  const vastData = adInfo.vastData;
  if (isNotNullish(vastData)) {
    state.adSystem = vastData.adSystem;
    state.advertiserName = vastData.advertiserName;
    state.apiFramework = vastData.apiFramework;
    state.creativeAdId = vastData.creativeAdId;
    state.creativeId = vastData.creativeId;
    state.universalAdIdRegistry = vastData.universalAdIdRegistry;
    state.universalAdIdValue = vastData.universalAdIdValue;
    state.adDescription = vastData.adDescription;
    state.minSuggestedDuration = Utils.calculateTimeWithUndefined(vastData.minSuggestedDuration);
    state.surveyUrl = vastData.surveyUrl;
    state.adTitle = vastData.adTitle;
    state.wrapperAdsCount = vastData.wrapperAdsCount;
  }

  if (isNotNullish(adInfo.skippableAfter)) {
    state.adSkipAfter =
      adInfo.skippableAfter === -1 ? adInfo.skippableAfter : Utils.calculateTime(adInfo.skippableAfter);
  }

  state.adSkippable = adInfo.skippable ?? (isNotNullish(state.adSkipAfter) ? state.adSkipAfter >= 0 : undefined);
  state.adDuration = Utils.calculateTimeWithUndefined(adInfo.durationInSec);

  return state;
}
